import * as React from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

import Spinner from 'components/spinner/Spinner';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import IntersectionChart from 'components/graphs/intersection-chart/IntersectionChart';
import VerticalSpacing from 'components/vertical-spacing/VerticalSpacing';

import StatisticsAudienceTotal from '../StatisticsAudienceTotal';
import StatisticsAudienceOverlapError from '../StatisticsAudienceOverlapError';

import {
  AUDIENCE_OVERLAP_STATUSES,
  resetCustomizableAudienceOverlap
} from 'modules/statistics/reducers/statistics-customizable-audience-overlap';

import { __t, applyTranslations } from 'utils/language';
import { abbrNum } from 'utils/helper/common';
import { getCustomizableAudienceOverlapData } from 'utils/charts/intersection-chart';

import './statistics-customizable-overlap.scss';

applyTranslations(require('./statistics-customizable-overlap.i18n.json'));

import type {
  Data,
  InfluencerCommon,
  MixedSelectDropdownItem,
  StatisticsCommon
} from 'types';

type OwnProps = {
  fetchCustomizableAudienceOverlap: (a: number) => Promise<void>
};

type StateProps = {
  creator: StatisticsCommon,
  others: StatisticsCommon,
  overlap: StatisticsCommon,
  influencers: Array<InfluencerCommon>,
  overlapStatus: string
};

type DispatchProps = {
  dispatchResetCustomizableAudienceOverlap: () => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  selectedInfluencerId: number
};

const MSG_KEY = 'statistics_customizable_overlap';

class StatisticsCustomizableOverlap extends React.PureComponent<Props, State> {
  state = {
    selectedInfluencerId: this.getFirstInfluencerId()
  };

  componentDidMount(): void {
    const {
      fetchCustomizableAudienceOverlap,
      influencers
    } = this.props;

    if (!isEmpty(influencers)) {
      fetchCustomizableAudienceOverlap(influencers[0].id);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { influencers, fetchCustomizableAudienceOverlap } = this.props;

    if (prevProps.influencers !== influencers && !isEmpty(influencers)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ selectedInfluencerId: influencers[0].id });
      fetchCustomizableAudienceOverlap(influencers[0].id);
    }
  }

  componentWillUnmount() {
    const {
      dispatchResetCustomizableAudienceOverlap
    } = this.props;

    dispatchResetCustomizableAudienceOverlap();
  }

  getFirstInfluencerId() {
    const { influencers } = this.props;

    return isEmpty(influencers) ? 0 : this.props.influencers[0].id;
  }

  getDropdownItems = (): Array<MixedSelectDropdownItem> => {
    const { influencers } = this.props;
    const { selectedInfluencerId } = this.state;

    return influencers.map(influencer => {
      const instagramChannel = influencer.channels.instagram;
      let label = influencer.name;

      if (instagramChannel) {
        const username = instagramChannel.username || '';
        const followers = instagramChannel.metrics && instagramChannel.metrics.followers || 0;

        label = `@${username} (${abbrNum(followers)} followers)`;
      }

      const influencerIdAsString = influencer.id.toString();

      return {
        identifier: influencerIdAsString,
        isDisabled: false,
        isSelected: selectedInfluencerId === influencer.id,
        label,
        value: influencerIdAsString
      };
    });
  };

  getTotalAudience() {
    const {
      creator,
      others,
      overlap
    } = this.props;

    return creator.absolute + others.absolute + 2 * overlap.absolute;
  }

  handleInfluencerChange = (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null,
    selectedItem?: Partial<MixedSelectDropdownItem> | null
  ): void => {
    const { fetchCustomizableAudienceOverlap } = this.props;
    const selectedInfluencerId = Number(selectedItem?.identifier || updatedItems[0].identifier);

    this.setState({ selectedInfluencerId });

    fetchCustomizableAudienceOverlap(selectedInfluencerId);
  };

  renderSpinner(): React.ReactNode {
    return (
      <div className="statistics-customizable-overlap__spinner">
        <Spinner
          position="inline"
          size="small"
          color="light"
        />
      </div>
    );
  }

  renderOverlap(): React.ReactNode {
    const {
      creator,
      others,
      overlap
    } = this.props;
    const { item1, item2, overlap: overlapItem } = getCustomizableAudienceOverlapData(creator, others, overlap);

    return (
      <div>
        <IntersectionChart
          item1={item1}
          item2={item2}
          overlap={overlapItem}
          maxSize={140}
        />
        <VerticalSpacing type="medium" />
        <StatisticsAudienceTotal totalAudience={this.getTotalAudience()} />
      </div>
    );
  }

  renderContent(): React.ReactNode {
    const { overlapStatus } = this.props;

    if (overlapStatus === AUDIENCE_OVERLAP_STATUSES.LOADING) {
      return this.renderSpinner();
    }

    if (overlapStatus === AUDIENCE_OVERLAP_STATUSES.AVAILABLE) {
      return this.renderOverlap();
    }

    return <StatisticsAudienceOverlapError error={overlapStatus} />;
  }

  render(): React.ReactNode {
    const { selectedInfluencerId } = this.state;
    const dropdownItems = this.getDropdownItems();

    return Boolean(dropdownItems.length) && (
      <React.Fragment>
        <div className="statistics-customizable-overlap__filter">
          <label
            className="statistics-customizable-overlap__filter-label"
            htmlFor="statistics-customizable-overlap-influencers"
          >
            {__t(`${MSG_KEY}.creators`)}:
          </label>
          <MixedSelectDropdown
            key={`statistics-customizable-overlap__dropdown--${selectedInfluencerId}`}
            name="statistics-customizable-overlap__dropdown"
            items={dropdownItems}
            withSelectedInList
            className="statistics-customizable-overlap__dropdown"
            onItemsToggle={this.handleInfluencerChange}
          />
        </div>
        {this.renderContent()}
      </React.Fragment>
    );
  }
}

const mapStateToProps = ({ statisticsCustomizableAudienceOverlap }): StateProps => ({
  ...statisticsCustomizableAudienceOverlap
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchResetCustomizableAudienceOverlap: (): void => dispatch(resetCustomizableAudienceOverlap())
});

export default connect(mapStateToProps, mapDispatchToProps)(StatisticsCustomizableOverlap);
